import { FC, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  Image,
  MiDetailFields,
  MiFileUpload,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  COST_CALCULATION_TYPE,
  INPUT_TYPES,
  MODAL_TITLES,
  VALID_FILE_MIME_TYPES,
} from "@miplatform/constants";
import { mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { Box } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IItemData, IItemDocument } from "../../Items.types";
import { useItemCostPricingTabViewModel } from "./useItemCostPricingTabViewModel";

export const ItemCostPricingTab: FC<ItemPreviewTabProps> = ({
  form,
  extraTabProps = {},
}: ItemPreviewTabProps) => {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { control, watch, setValue } = form;
  const { isReadOnly } = extraTabProps;
  const { classes, theme } = useStyles();

  const {
    isLoading,
    quoteRef,
    deleteQuote,
    handleUpdateQuote,
    handleUploadQuote,
  } = useItemCostPricingTabViewModel({ setValue, watch });

  const {
    category: { categoryId } = {},
    costCalculation,
    manufacturerListPrice,
    multiplier = 0,
    taxRate = 0,
    cost,
    quoteDocument = {} as IItemDocument,
  } = watch();
  const { id: quoteDocumentId } = quoteDocument;

  const calculateTotalCost = () => {
    if (costCalculation === COST_CALCULATION_TYPE.COST_PLUS) {
      return manufacturerListPrice + (manufacturerListPrice * taxRate) / 100;
    }
    const multiplierAmount = (manufacturerListPrice * multiplier) / 100;
    const taxAmount = (manufacturerListPrice * taxRate) / 100;
    return manufacturerListPrice + multiplierAmount + taxAmount;
  };

  useEffect(() => {
    setValue("cost", calculateTotalCost(), {
      shouldValidate: true,
    });
  }, [costCalculation, manufacturerListPrice, multiplier, taxRate]);

  const commonDisabled = !categoryId || isReadOnly;

  const costPricingFields = () => {
    const fields = [];

    fields.push(
      {
        fieldType: INPUT_TYPES.RADIO_GROUP,
        name: "costCalculation",
        formControl: control,
        width: "100%",
        disabled: commonDisabled,
        options: [
          { label: "Cost Plus", value: COST_CALCULATION_TYPE.COST_PLUS },
          { label: "List Down", value: COST_CALCULATION_TYPE.LIST_DOWN },
        ],
        setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
          setValue("costCalculation", e.target.value, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue("multiplier", 0, {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      },
      {
        fieldType: INPUT_TYPES.NUMERIC,
        label: "Supplier Price ($)",
        name: "manufacturerListPrice",
        value: manufacturerListPrice,
        formControl: control,
        prefix: "$",
        decimalScale: 2,
        width: "25%",
        disabled: commonDisabled,
        required: true,
        setValue: (value: number) => {
          setValue("manufacturerListPrice", value, {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      }
    );

    if (costCalculation === "ListDown") {
      fields.push({
        fieldType: INPUT_TYPES.NUMERIC,
        label: "Multiplier(%)",
        name: "multiplier",
        formControl: control,
        suffix: "%",
        decimalScale: 2,
        width: "25%",
        disabled: commonDisabled,
        required: true,
        value: multiplier,
        setValue: (value: number) => {
          setValue("multiplier", value, {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      });
    }

    fields.push(
      {
        fieldType: INPUT_TYPES.NUMERIC,
        label: "Tax Rate (%)",
        name: "taxRate",
        formControl: control,
        suffix: "%",
        decimalScale: 2,
        width: "25%",
        disabled: commonDisabled,
        required: true,
        value: taxRate,
        setValue: (value: number) => {
          setValue("taxRate", value, {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      },
      {
        fieldType: INPUT_TYPES.NUMERIC,
        label: "Cost ($)",
        name: "cost",
        formControl: control,
        prefix: "$",
        decimalScale: 2,
        width: "25%",
        value: cost,
        setValue: () => null,
        disabled: true,
      } as any
    );

    return fields;
  };

  return (
    <ConfigAccordionHeader title="Item Cost Pricing">
      <MiDetailFields detailFields={costPricingFields()} />
      <Box className={classes.fileUploadContainer}>
        {quoteDocumentId ? (
          <Box className={classes.fileWrapper}>
            <Image
              image={quoteDocument}
              containerClass={classes.imageContainer}
              isEditing={!isReadOnly}
              onEdit={categoryId ? () => quoteRef.current?.click() : undefined}
              onDelete={() => setOpenDeleteModal(true)}
              fileName={quoteDocument?.title || quoteDocument.metadata?.title}
            />
          </Box>
        ) : (
          <MiFileUpload
            dragAndDrop
            acceptableFormatText="Acceptable formats: png, jpg, pdf"
            showUploadButton
            buttonStartIcon={{ path: mdiPlus }}
            title="Upload Quote"
            fullWidth
            validFileTypes={VALID_FILE_MIME_TYPES}
            onChange={(files: FileList) => {
              if (files.length > 0) {
                handleUploadQuote(files[0]);
              }
            }}
            acceptFiles={VALID_FILE_MIME_TYPES.join(",")}
            disabled={commonDisabled}
          />
        )}
        <input
          ref={quoteRef}
          type="file"
          data-testid="quote-file-input"
          accept={VALID_FILE_MIME_TYPES.join(",")}
          onChange={(e) => handleUpdateQuote(e)}
          className={classes.fileInput}
        />
      </Box>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        textPositive={BUTTON_LABELS.DELETE}
        buttonPositiveColor={BUTTON_COLORS.RED}
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.red.main,
        }}
        open={openDeleteModal}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={() => {
          deleteQuote();
          setOpenDeleteModal(false);
        }}
        description="You are about to delete the quote from this item. Are you sure you want to delete this quote?"
        disableClick={isLoading}
      />
    </ConfigAccordionHeader>
  );
};

const useStyles = makeStyles()((theme) => ({
  imageContainer: {
    minWidth: 200,
    minHeight: 200,
    maxWidth: 200,
    maxHeight: 200,
    margin: theme.spacing(1, 0, 0, 1),
    borderRadius: 6,
  },
  fileUploadContainer: {
    display: "flex",
    margin: theme.spacing(1),
  },
  fileWrapper: {
    border: `1px dashed ${theme.palette.grey.accent}`,
    width: "100%",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
  },
  fileInput: {
    display: "none",
  },
}));

interface ItemPreviewTabProps {
  form: UseFormReturn<IItemData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}
